import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Play, Lock, ArrowLeft, ArrowRight, BookOpen, Clock, CheckCircle, List } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WatchCoursePage = () => {
  const { courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [error, setError] = useState(null);
  const [showLessons, setShowLessons] = useState(false);
  const { user, token } = useAuth();
  const navigate = useNavigate();

  // Sample course data (will be replaced with API)
  const sampleCourseData = {
    'course-1': {
      id: 'course-1',
      title: 'Emocinio intelekto pagrindai',
      description: 'Išsamus video kursas apie emocinio intelekto ugdymą.',
      lessons: [
        { id: 1, title: 'Įvadas: Kas yra emocinis intelektas?', duration: '15:00', vimeo_id: '' },
        { id: 2, title: 'Savęs pažinimas', duration: '30:00', vimeo_id: '' },
        { id: 3, title: 'Emocijų atpažinimas', duration: '25:00', vimeo_id: '' },
        { id: 4, title: 'Emocijų valdymas', duration: '35:00', vimeo_id: '' },
        { id: 5, title: 'Motyvacija', duration: '20:00', vimeo_id: '' },
        { id: 6, title: 'Empatija', duration: '30:00', vimeo_id: '' },
        { id: 7, title: 'Socialiniai įgūdžiai', duration: '40:00', vimeo_id: '' },
        { id: 8, title: 'EQ darbe', duration: '35:00', vimeo_id: '' },
        { id: 9, title: 'EQ santykiuose', duration: '30:00', vimeo_id: '' },
        { id: 10, title: 'Apibendrinimas ir tolimesni žingsniai', duration: '20:00', vimeo_id: '' }
      ],
      color: 'bg-[#4CAF50]',
      lightColor: 'bg-[#E8F5E9]'
    },
    'course-2': {
      id: 'course-2',
      title: 'Streso valdymo meistras',
      description: 'Praktinis kursas su konkrečiomis technikomis kasdieniam streso valdymui.',
      lessons: [
        { id: 1, title: 'Įvadas: Supratimas apie stresą', duration: '20:00', vimeo_id: '' },
        { id: 2, title: 'Streso požymiai ir simptomai', duration: '25:00', vimeo_id: '' },
        { id: 3, title: 'Kvėpavimo technikos', duration: '15:00', vimeo_id: '' },
        { id: 4, title: 'Progresyvi raumenų relaksacija', duration: '20:00', vimeo_id: '' },
        { id: 5, title: 'Mindfulness praktika', duration: '30:00', vimeo_id: '' },
        { id: 6, title: 'Laiko planavimas', duration: '25:00', vimeo_id: '' },
        { id: 7, title: 'Ribų nustatymas', duration: '20:00', vimeo_id: '' },
        { id: 8, title: 'Ilgalaikė streso prevencija', duration: '25:00', vimeo_id: '' }
      ],
      color: 'bg-[#FF9F43]',
      lightColor: 'bg-[#FFF3E0]'
    },
    'course-3': {
      id: 'course-3',
      title: 'Sąmoningas bendravimas',
      description: 'Kursas apie efektyvų ir sąmoningą bendravimą.',
      lessons: [
        { id: 1, title: 'Įvadas į sąmoningą bendravimą', duration: '15:00', vimeo_id: '' },
        { id: 2, title: 'Aktyvus klausymasis', duration: '25:00', vimeo_id: '' },
        { id: 3, title: 'Neverbalinis bendravimas', duration: '20:00', vimeo_id: '' },
        { id: 4, title: 'Empatiškas atsakymas', duration: '30:00', vimeo_id: '' },
        { id: 5, title: 'Konstruktyvus grįžtamasis ryšys', duration: '25:00', vimeo_id: '' },
        { id: 6, title: 'Konfliktų sprendimas', duration: '35:00', vimeo_id: '' },
        { id: 7, title: 'Asertyvumas', duration: '30:00', vimeo_id: '' },
        { id: 8, title: 'Sudėtingi pokalbiai', duration: '25:00', vimeo_id: '' },
        { id: 9, title: 'Bendravimas komandoje', duration: '20:00', vimeo_id: '' },
        { id: 10, title: 'Skaitmeninis bendravimas', duration: '15:00', vimeo_id: '' },
        { id: 11, title: 'Viešasis kalbėjimas', duration: '30:00', vimeo_id: '' },
        { id: 12, title: 'Apibendrinimas', duration: '15:00', vimeo_id: '' }
      ],
      color: 'bg-[#5B9BD5]',
      lightColor: 'bg-[#E3F2FD]'
    }
  };

  useEffect(() => {
    if (!user || !token) {
      navigate(`/prisijungti?redirect=/kursai/${courseId}/ziureti`);
      return;
    }
    checkAccessAndLoadCourse();
  }, [user, token, courseId, navigate]);

  const checkAccessAndLoadCourse = async () => {
    try {
      // Check access
      const accessResponse = await axios.get(`${API_URL}/api/courses/${courseId}/access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (accessResponse.data.has_access) {
        setHasAccess(true);
        
        // Load course data
        try {
          const courseResponse = await axios.get(`${API_URL}/api/courses/${courseId}`);
          setCourse(courseResponse.data);
        } catch {
          // Use sample data if API fails
          setCourse(sampleCourseData[courseId] || null);
        }
      } else {
        setHasAccess(false);
        setError('Neturite prieigos prie šio kurso');
      }
    } catch (error) {
      // For demo purposes, show sample data
      if (sampleCourseData[courseId]) {
        setCourse(sampleCourseData[courseId]);
        setHasAccess(true); // Demo mode - allow access
      } else {
        setError('Kursas nerastas arba neturite prieigos');
      }
    } finally {
      setLoading(false);
    }
  };

  const goToNextLesson = () => {
    if (currentLesson < course.lessons.length - 1) {
      setCurrentLesson(currentLesson + 1);
    }
  };

  const goToPrevLesson = () => {
    if (currentLesson > 0) {
      setCurrentLesson(currentLesson - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#4CAF50] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !hasAccess) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-[#FFEBEE] rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-10 h-10 text-[#FF6B6B]" />
          </div>
          <h1 className="text-2xl font-['Poppins'] font-bold text-[#2D3748] mb-4">
            Prieiga uždrausta
          </h1>
          <p className="text-[#718096] mb-8">
            {error || 'Norėdami žiūrėti šį kursą, turite jį įsigyti.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kursai"
              className="inline-flex items-center justify-center bg-[#4CAF50] text-white hover:bg-[#43A047] rounded-full px-6 py-3 font-semibold transition-colors"
            >
              Peržiūrėti kursus
            </Link>
            <Link
              to="/mano-kursai"
              className="inline-flex items-center justify-center bg-white text-[#2D3748] border border-slate-200 hover:border-[#4CAF50] rounded-full px-6 py-3 font-medium transition-colors"
            >
              Mano kursai
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <p className="text-[#718096]">Kursas nerastas</p>
      </div>
    );
  }

  const currentLessonData = course.lessons[currentLesson];

  return (
    <div data-testid="watch-course-page" className="min-h-screen bg-[#1a1a2e]">
      {/* Header */}
      <header className="bg-[#16162a] border-b border-white/10 py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link 
            to="/mano-kursai" 
            className="flex items-center text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            <span className="hidden sm:inline">Grįžti į kursus</span>
          </Link>
          
          <h1 className="text-white font-medium text-sm sm:text-base truncate max-w-xs sm:max-w-md">
            {course.title}
          </h1>

          <button
            onClick={() => setShowLessons(!showLessons)}
            className="flex items-center text-white/70 hover:text-white transition-colors lg:hidden"
          >
            <List className="w-5 h-5" />
          </button>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row">
        {/* Video Player Area */}
        <div className="flex-1">
          {/* Video Container */}
          <div className="relative bg-black aspect-video">
            {currentLessonData.vimeo_id ? (
              <iframe
                src={`https://player.vimeo.com/video/${currentLessonData.vimeo_id}?h=0&title=0&byline=0&portrait=0`}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={currentLessonData.title}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className={`w-24 h-24 ${course.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                    <Play className="w-12 h-12 text-white ml-1" />
                  </div>
                  <p className="text-white/70 text-lg">Video bus pridėtas netrukus</p>
                  <p className="text-white/50 text-sm mt-2">Pamoka: {currentLessonData.title}</p>
                </div>
              </div>
            )}
          </div>

          {/* Lesson Info & Navigation */}
          <div className="bg-[#16162a] p-4 sm:p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-[#4CAF50] text-sm mb-1">
                  Pamoka {currentLesson + 1} iš {course.lessons.length}
                </p>
                <h2 className="text-white text-xl sm:text-2xl font-semibold">
                  {currentLessonData.title}
                </h2>
              </div>
              <span className="text-white/50 text-sm flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {currentLessonData.duration}
              </span>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between">
              <button
                onClick={goToPrevLesson}
                disabled={currentLesson === 0}
                className="flex items-center text-white/70 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                <span className="hidden sm:inline">Ankstesnė pamoka</span>
              </button>

              <button
                onClick={goToNextLesson}
                disabled={currentLesson === course.lessons.length - 1}
                className="flex items-center text-white/70 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <span className="hidden sm:inline">Kita pamoka</span>
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </div>
        </div>

        {/* Lessons Sidebar */}
        <div className={`lg:w-80 xl:w-96 bg-[#16162a] border-l border-white/10 ${
          showLessons ? 'block' : 'hidden lg:block'
        }`}>
          <div className="p-4 border-b border-white/10">
            <h3 className="text-white font-semibold flex items-center">
              <BookOpen className="w-5 h-5 mr-2" />
              Kurso turinys
            </h3>
            <p className="text-white/50 text-sm mt-1">
              {course.lessons.length} pamokos
            </p>
          </div>

          <div className="overflow-y-auto max-h-[calc(100vh-300px)] lg:max-h-[calc(100vh-200px)]">
            {course.lessons.map((lesson, index) => (
              <button
                key={lesson.id}
                onClick={() => {
                  setCurrentLesson(index);
                  setShowLessons(false);
                }}
                className={`w-full p-4 text-left border-b border-white/5 transition-colors ${
                  index === currentLesson
                    ? 'bg-[#4CAF50]/20 border-l-2 border-l-[#4CAF50]'
                    : 'hover:bg-white/5'
                }`}
              >
                <div className="flex items-start">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${
                    index === currentLesson ? 'bg-[#4CAF50]' : 'bg-white/10'
                  }`}>
                    {index < currentLesson ? (
                      <CheckCircle className="w-4 h-4 text-white" />
                    ) : index === currentLesson ? (
                      <Play className="w-4 h-4 text-white ml-0.5" />
                    ) : (
                      <span className="text-white/70 text-sm">{index + 1}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${
                      index === currentLesson ? 'text-white' : 'text-white/70'
                    }`}>
                      {lesson.title}
                    </p>
                    <p className="text-white/40 text-xs mt-1">
                      {lesson.duration}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WatchCoursePage;
